import { Masterplan } from '../services/api';

interface StreamData {
    stream_name: string;
    keywords?: {
        core_local?: string[];
        jurisdiction_specific?: string[];
        niche?: string[];
        long_tail?: string[];
        local_boosters?: string[];
    };
    keyword_rationale?: string;
    competitor_sources?: Array<{
        name: string;
        url: string;
        relevance: string;
    }>;
    competitor_rationale?: string;
    articles?: Array<{
        title: string;
        summary: string;
        prompt: string;
        target_keywords?: string[];
        word_count?: string;
        structural_requirements?: string[];
    }>;
}

export const generateMasterplanHTML = (masterplan: Masterplan): string => {
    const streams = masterplan.streams as StreamData[] || [];
    const createdDate = new Date(masterplan.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Content Masterplan - ${masterplan.industry || 'Business'}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #1f2937;
            background: #f9fafb;
            padding: 40px 20px;
        }

        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            padding: 60px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            border-radius: 8px;
        }

        .header {
            border-bottom: 3px solid #3b82f6;
            padding-bottom: 30px;
            margin-bottom: 40px;
        }

        .header h1 {
            font-size: 2.5rem;
            color: #111827;
            margin-bottom: 10px;
        }

        .header .subtitle {
            font-size: 1.25rem;
            color: #6b7280;
        }

        .header .meta {
            margin-top: 20px;
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
            font-size: 0.875rem;
            color: #4b5563;
        }

        .header .meta-item {
            background: #f3f4f6;
            padding: 10px 15px;
            border-radius: 6px;
        }

        .header .meta-label {
            font-weight: 600;
            color: #374151;
            display: block;
            margin-bottom: 4px;
        }

        .section {
            margin-bottom: 50px;
        }

        .section-title {
            font-size: 1.75rem;
            color: #111827;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #e5e7eb;
        }

        .stream {
            background: #f9fafb;
            border-left: 4px solid #3b82f6;
            padding: 30px;
            margin-bottom: 40px;
            border-radius: 6px;
        }

        .stream-title {
            font-size: 1.5rem;
            color: #1e40af;
            margin-bottom: 20px;
        }

        .subsection {
            margin-bottom: 30px;
        }

        .subsection-title {
            font-size: 1.125rem;
            color: #374151;
            font-weight: 600;
            margin-bottom: 15px;
        }

        .keyword-category {
            margin-bottom: 20px;
        }

        .keyword-category-name {
            font-weight: 600;
            color: #4b5563;
            margin-bottom: 8px;
        }

        .keyword-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
        }

        .keyword-tag {
            background: #eff6ff;
            border: 1px solid #bfdbfe;
            color: #1e40af;
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 0.875rem;
        }

        .rationale {
            background: #fff;
            border-left: 3px solid #10b981;
            padding: 15px 20px;
            margin: 15px 0;
            border-radius: 4px;
            font-style: italic;
            color: #4b5563;
        }

        .competitor-list {
            display: grid;
            gap: 15px;
        }

        .competitor-item {
            background: white;
            border: 1px solid #e5e7eb;
            padding: 15px;
            border-radius: 6px;
        }

        .competitor-name {
            font-weight: 600;
            color: #111827;
            margin-bottom: 4px;
        }

        .competitor-url {
            color: #3b82f6;
            font-size: 0.875rem;
            margin-bottom: 8px;
            word-break: break-all;
        }

        .competitor-relevance {
            color: #6b7280;
            font-size: 0.875rem;
        }

        .article-roadmap {
            display: grid;
            gap: 20px;
        }

        .article-card {
            background: white;
            border: 1px solid #e5e7eb;
            padding: 20px;
            border-radius: 6px;
        }

        .article-number {
            display: inline-block;
            background: #3b82f6;
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-weight: 600;
            font-size: 0.875rem;
            margin-bottom: 10px;
        }

        .article-title {
            font-size: 1.125rem;
            font-weight: 600;
            color: #111827;
            margin-bottom: 10px;
        }

        .article-summary {
            color: #4b5563;
            margin-bottom: 15px;
            line-height: 1.5;
        }

        .article-meta {
            display: flex;
            gap: 20px;
            margin-bottom: 15px;
            font-size: 0.875rem;
        }

        .article-meta-item {
            background: #f3f4f6;
            padding: 6px 12px;
            border-radius: 4px;
            color: #374151;
        }

        .article-prompt {
            background: #f9fafb;
            padding: 15px;
            border-radius: 4px;
            font-size: 0.875rem;
            color: #4b5563;
            white-space: pre-wrap;
            font-family: 'Courier New', monospace;
        }

        .requirements-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 10px;
        }

        .requirement-tag {
            background: #fef3c7;
            border: 1px solid #fcd34d;
            color: #92400e;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 0.75rem;
        }

        .footer {
            margin-top: 60px;
            padding-top: 30px;
            border-top: 2px solid #e5e7eb;
            text-align: center;
            color: #6b7280;
            font-size: 0.875rem;
        }

        @media print {
            body {
                padding: 0;
                background: white;
            }
            .container {
                box-shadow: none;
                padding: 40px;
            }
            .stream {
                page-break-inside: avoid;
            }
            .article-card {
                page-break-inside: avoid;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Content Masterplan</h1>
            <div class="subtitle">${masterplan.industry || 'Business'} - ${masterplan.business_type || 'Strategy'}</div>
            <div class="meta">
                <div class="meta-item">
                    <span class="meta-label">Primary Location</span>
                    ${masterplan.primary_location || 'N/A'}
                </div>
                <div class="meta-item">
                    <span class="meta-label">Jurisdiction</span>
                    ${masterplan.jurisdiction || 'N/A'}
                </div>
                <div class="meta-item">
                    <span class="meta-label">Country</span>
                    ${masterplan.country || 'N/A'}
                </div>
                <div class="meta-item">
                    <span class="meta-label">Content Streams</span>
                    ${masterplan.num_streams || 0}
                </div>
                <div class="meta-item">
                    <span class="meta-label">Created</span>
                    ${createdDate}
                </div>
            </div>
        </div>

        ${streams.map((stream, index) => `
            <div class="stream">
                <h2 class="stream-title">Stream ${index + 1}: ${stream.stream_name}</h2>

                <div class="subsection">
                    <h3 class="subsection-title">Target Keywords</h3>

                    ${stream.keywords?.core_local && stream.keywords.core_local.length > 0 ? `
                        <div class="keyword-category">
                            <div class="keyword-category-name">Core Service + Local Keywords</div>
                            <div class="keyword-list">
                                ${stream.keywords.core_local.map(kw => `<span class="keyword-tag">${kw}</span>`).join('')}
                            </div>
                        </div>
                    ` : ''}

                    ${stream.keywords?.jurisdiction_specific && stream.keywords.jurisdiction_specific.length > 0 ? `
                        <div class="keyword-category">
                            <div class="keyword-category-name">Jurisdiction-Specific Intent Keywords</div>
                            <div class="keyword-list">
                                ${stream.keywords.jurisdiction_specific.map(kw => `<span class="keyword-tag">${kw}</span>`).join('')}
                            </div>
                        </div>
                    ` : ''}

                    ${stream.keywords?.niche && stream.keywords.niche.length > 0 ? `
                        <div class="keyword-category">
                            <div class="keyword-category-name">Niche/Specialization Keywords</div>
                            <div class="keyword-list">
                                ${stream.keywords.niche.map(kw => `<span class="keyword-tag">${kw}</span>`).join('')}
                            </div>
                        </div>
                    ` : ''}

                    ${stream.keywords?.long_tail && stream.keywords.long_tail.length > 0 ? `
                        <div class="keyword-category">
                            <div class="keyword-category-name">Long-Tail Keywords</div>
                            <div class="keyword-list">
                                ${stream.keywords.long_tail.map(kw => `<span class="keyword-tag">${kw}</span>`).join('')}
                            </div>
                        </div>
                    ` : ''}

                    ${stream.keywords?.local_boosters && stream.keywords.local_boosters.length > 0 ? `
                        <div class="keyword-category">
                            <div class="keyword-category-name">Local SEO Boosters</div>
                            <div class="keyword-list">
                                ${stream.keywords.local_boosters.map(kw => `<span class="keyword-tag">${kw}</span>`).join('')}
                            </div>
                        </div>
                    ` : ''}

                    ${stream.keyword_rationale ? `
                        <div class="rationale">
                            <strong>Strategy Rationale:</strong> ${stream.keyword_rationale}
                        </div>
                    ` : ''}
                </div>

                ${stream.competitor_sources && stream.competitor_sources.length > 0 ? `
                    <div class="subsection">
                        <h3 class="subsection-title">Competitor & Authority Sources</h3>
                        <div class="competitor-list">
                            ${stream.competitor_sources.map(comp => `
                                <div class="competitor-item">
                                    <div class="competitor-name">${comp.name}</div>
                                    <div class="competitor-url">${comp.url}</div>
                                    <div class="competitor-relevance">${comp.relevance}</div>
                                </div>
                            `).join('')}
                        </div>
                        ${stream.competitor_rationale ? `
                            <div class="rationale">
                                <strong>Selection Rationale:</strong> ${stream.competitor_rationale}
                            </div>
                        ` : ''}
                    </div>
                ` : ''}

                ${stream.articles && stream.articles.length > 0 ? `
                    <div class="subsection">
                        <h3 class="subsection-title">10-Article Content Roadmap</h3>
                        <div class="article-roadmap">
                            ${stream.articles.map((article, articleIndex) => `
                                <div class="article-card">
                                    <span class="article-number">Article ${articleIndex + 1}</span>
                                    <div class="article-title">${article.title}</div>
                                    <div class="article-summary">${article.summary}</div>
                                    <div class="article-meta">
                                        ${article.word_count ? `<span class="article-meta-item">📝 ${article.word_count} words</span>` : ''}
                                        ${article.target_keywords && article.target_keywords.length > 0 ? `<span class="article-meta-item">🎯 ${article.target_keywords.length} keywords</span>` : ''}
                                    </div>
                                    ${article.target_keywords && article.target_keywords.length > 0 ? `
                                        <div class="keyword-list">
                                            ${article.target_keywords.map(kw => `<span class="keyword-tag">${kw}</span>`).join('')}
                                        </div>
                                    ` : ''}
                                    ${article.structural_requirements && article.structural_requirements.length > 0 ? `
                                        <div class="requirements-list">
                                            ${article.structural_requirements.map(req => `<span class="requirement-tag">${req}</span>`).join('')}
                                        </div>
                                    ` : ''}
                                    <div style="margin-top: 15px;">
                                        <strong style="display: block; margin-bottom: 8px; color: #374151;">Content Generation Prompt:</strong>
                                        <div class="article-prompt">${article.prompt}</div>
                                    </div>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                ` : ''}
            </div>
        `).join('')}

        <div class="footer">
            <p>Generated on ${createdDate} | Masterplan ID: ${masterplan.masterplan_id}</p>
            <p style="margin-top: 10px;">This masterplan was created to guide your content strategy and SEO execution.</p>
        </div>
    </div>
</body>
</html>
    `.trim();

    return html;
};

export const downloadMasterplanHTML = (masterplan: Masterplan) => {
    const html = generateMasterplanHTML(masterplan);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `masterplan-${masterplan.industry || 'business'}-${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
